import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import type { WorkspaceBrandingRecord, WorkspaceRecord } from '../types';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

const STORAGE_BUCKET = 'workspace-branding';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const allowedImageTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);

type BrandingForm = {
  display_name: string;
  tagline: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string;
  portal_icon_url: string;
  app_name: string;
  short_name: string;
};

type Snack = {
  severity: 'success' | 'warning' | 'error';
  message: string;
};

const defaultForm: BrandingForm = {
  display_name: '',
  tagline: '',
  primary_color: '#1b5e20',
  secondary_color: '#f4f7f4',
  accent_color: '#1565c0',
  logo_url: '',
  portal_icon_url: '',
  app_name: '',
  short_name: '',
};

function formFromRecord(record: WorkspaceBrandingRecord | null, workspace: WorkspaceRecord | null): BrandingForm {
  return {
    display_name: record?.display_name || workspace?.name || '',
    tagline: record?.tagline || '',
    primary_color: record?.primary_color || defaultForm.primary_color,
    secondary_color: record?.secondary_color || defaultForm.secondary_color,
    accent_color: record?.accent_color || defaultForm.accent_color,
    logo_url: record?.logo_url || '',
    portal_icon_url: record?.portal_icon_url || '',
    app_name: record?.app_name || record?.display_name || workspace?.name || '',
    short_name: record?.short_name || record?.display_name || workspace?.name || '',
  };
}

function nullable(value: string): string | null {
  const normalized = value.trim();
  return normalized || null;
}

function fileExtension(file: File): string {
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  return 'jpg';
}

function storagePathFromPublicUrl(url: string): string | null {
  if (!url) return null;
  const marker = `/storage/v1/object/public/${STORAGE_BUCKET}/`;
  const markerIndex = url.indexOf(marker);
  if (markerIndex === -1) return null;
  const path = url.slice(markerIndex + marker.length).split('?')[0];
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('The selected image could not be read.'));
    };
    image.src = objectUrl;
  });
}

export function WorkspaceBranding({ isAdmin }: { isAdmin: boolean }) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'workspace-branding',
    defaultState: { workspaceId: '' },
  });
  const [workspaces, setWorkspaces] = useState<WorkspaceRecord[]>([]);
  const [brandingRecords, setBrandingRecords] = useState<WorkspaceBrandingRecord[]>([]);
  const [workspaceId, setWorkspaceId] = useState(savedViewState.workspaceId);
  const [form, setForm] = useState<BrandingForm>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'logo_url' | 'portal_icon_url' | null>(null);
  const [snack, setSnack] = useState<Snack | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'workspace-branding', ready: !loading && !saving && !uploading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setWorkspaceId(savedViewState.workspaceId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ workspaceId });
  }, [setSavedViewState, viewStateKey, viewStateReady, workspaceId]);

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === workspaceId) || null,
    [workspaceId, workspaces],
  );
  const selectedBranding = useMemo(
    () => brandingRecords.find((branding) => branding.workspace_id === workspaceId) || null,
    [brandingRecords, workspaceId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    const [workspacesResult, brandingResult] = await Promise.all([
      supabase.from('workspaces').select('*').eq('active_status', true).order('name'),
      supabase.from('workspace_branding').select('*').order('created_at'),
    ]);
    setLoading(false);

    const error = workspacesResult.error || brandingResult.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const nextWorkspaces = (workspacesResult.data || []) as WorkspaceRecord[];
    const nextBranding = (brandingResult.data || []) as WorkspaceBrandingRecord[];
    setWorkspaces(nextWorkspaces);
    setBrandingRecords(nextBranding);
    setWorkspaceId((current) => (current && nextWorkspaces.some((workspace) => workspace.id === current) ? current : nextWorkspaces[0]?.id || ''));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setForm(formFromRecord(selectedBranding, selectedWorkspace));
  }, [selectedBranding, selectedWorkspace]);

  const setField = (field: keyof BrandingForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    if (!isAdmin || !workspaceId) return;
    if (!form.display_name.trim()) {
      setSnack({ severity: 'error', message: 'Display Name is required.' });
      return;
    }
    if (!form.app_name.trim() || !form.short_name.trim()) {
      setSnack({ severity: 'error', message: 'App Name and Short Name are required.' });
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from('workspace_branding')
      .upsert({
        workspace_id: workspaceId,
        display_name: nullable(form.display_name),
        tagline: nullable(form.tagline),
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        accent_color: form.accent_color,
        logo_url: nullable(form.logo_url),
        portal_icon_url: nullable(form.portal_icon_url),
        app_name: nullable(form.app_name),
        short_name: nullable(form.short_name),
      }, { onConflict: 'workspace_id' });
    setSaving(false);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Workspace branding saved.' });
    await load();
  };

  const uploadImage = async (field: 'logo_url' | 'portal_icon_url', file: File | null) => {
    if (!file || !workspaceId || !isAdmin) return;
    if (!allowedImageTypes.has(file.type)) {
      setSnack({ severity: 'error', message: 'Choose a PNG, JPG, or WebP image.' });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setSnack({ severity: 'error', message: 'Images must be 5 MB or smaller.' });
      return;
    }

    try {
      const dimensions = await getImageDimensions(file);
      if (field === 'portal_icon_url' && (dimensions.width < 512 || dimensions.height < 512)) {
        setSnack({ severity: 'error', message: 'Portal icons must be at least 512 × 512 pixels.' });
        return;
      }
      if (field === 'portal_icon_url' && dimensions.width !== dimensions.height) {
        setSnack({ severity: 'error', message: 'Portal icons must be square.' });
        return;
      }
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'The selected image could not be read.' });
      return;
    }

    setUploading(field);
    const extension = fileExtension(file);
    const imageKind = field === 'logo_url' ? 'logo' : 'portal-icon';
    const path = `${workspaceId}/${imageKind}-${Date.now()}.${extension}`;
    const uploadResult = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
      cacheControl: '31536000',
      contentType: file.type,
      upsert: false,
    });

    if (uploadResult.error) {
      setUploading(null);
      setSnack({ severity: 'error', message: uploadResult.error.message });
      return;
    }

    const publicUrl = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path).data.publicUrl;
    const previousUrl = form[field];
    const { error: saveError } = await supabase
      .from('workspace_branding')
      .upsert({ workspace_id: workspaceId, [field]: publicUrl }, { onConflict: 'workspace_id' });

    if (saveError) {
      await supabase.storage.from(STORAGE_BUCKET).remove([path]);
      setUploading(null);
      setSnack({ severity: 'error', message: saveError.message });
      return;
    }

    const previousPath = storagePathFromPublicUrl(previousUrl);
    const previousRemoval = previousPath
      ? await supabase.storage.from(STORAGE_BUCKET).remove([previousPath])
      : null;

    setForm((current) => ({ ...current, [field]: publicUrl }));
    setUploading(null);
    setSnack({
      severity: previousRemoval?.error ? 'warning' : 'success',
      message: previousRemoval?.error
        ? 'The new image is active, but the previous image could not be removed from Storage.'
        : field === 'logo_url' ? 'Workspace logo uploaded.' : 'Portal icon uploaded.',
    });
  };

  const removeImage = async (field: 'logo_url' | 'portal_icon_url') => {
    if (!workspaceId || !isAdmin || !form[field]) return;
    const currentUrl = form[field];
    const { error } = await supabase
      .from('workspace_branding')
      .update({ [field]: null })
      .eq('workspace_id', workspaceId);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const path = storagePathFromPublicUrl(currentUrl);
    let removalFailed = false;
    if (path) {
      const removal = await supabase.storage.from(STORAGE_BUCKET).remove([path]);
      removalFailed = Boolean(removal.error);
    }
    setForm((current) => ({ ...current, [field]: '' }));
    setSnack(removalFailed
      ? { severity: 'warning', message: 'Branding was updated, but the previous image could not be removed from Storage.' }
      : { severity: 'success', message: field === 'logo_url' ? 'Workspace logo removed.' : 'Portal icon removed.' });
  };

  if (!isAdmin) {
    return <Alert severity="warning">Workspace branding can only be managed by an administrator.</Alert>;
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 920 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Workspace Branding</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Define the identity shared by the player portal and future workspace communications.
        </Typography>
      </Box>

      {loading && <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Paper>}

      {!loading && workspaces.length === 0 && (
        <Alert severity="warning">No active workspace is available. Apply the workspace branding migration first.</Alert>
      )}

      {!loading && workspaces.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Workspace</InputLabel>
              <Select label="Workspace" value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
                {workspaces.map((workspace) => (
                  <MenuItem key={workspace.id} value={workspace.id}>{workspace.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Identity</Typography>
              <TextField label="Display Name" value={form.display_name} required onChange={(event) => setField('display_name', event.target.value)} />
              <TextField label="Tagline" value={form.tagline} onChange={(event) => setField('tagline', event.target.value)} />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Colors</Typography>
              <Typography variant="body2" color="text.secondary">
                These colors control the player portal header, background, accents, and install metadata.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <TextField label="Primary Color" type="color" value={form.primary_color} onChange={(event) => setField('primary_color', event.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Secondary Color" type="color" value={form.secondary_color} onChange={(event) => setField('secondary_color', event.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Accent Color" type="color" value={form.accent_color} onChange={(event) => setField('accent_color', event.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Images</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  PNG, JPG, or WebP up to 5 MB. Portal icons must be square and at least 512 × 512 pixels.
                </Typography>
              </Box>
              <Divider />
              <BrandImageControl
                label="Workspace Logo"
                purpose="Used in branded headers and future communications."
                value={form.logo_url}
                busy={uploading === 'logo_url'}
                onUpload={(file) => void uploadImage('logo_url', file)}
                onRemove={() => void removeImage('logo_url')}
              />
              <Divider />
              <BrandImageControl
                label="Workspace Portal Icon"
                purpose="Used for the player portal and supported home-screen installs."
                value={form.portal_icon_url}
                busy={uploading === 'portal_icon_url'}
                onUpload={(file) => void uploadImage('portal_icon_url', file)}
                onRemove={() => void removeImage('portal_icon_url')}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Home Screen App</Typography>
              <TextField label="App Name" value={form.app_name} required onChange={(event) => setField('app_name', event.target.value)} />
              <TextField
                label="Short Name"
                value={form.short_name}
                required
                helperText="Keep this concise so it fits beneath a phone home-screen icon."
                onChange={(event) => setField('short_name', event.target.value)}
              />
            </Stack>
          </Paper>

          <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save Workspace Branding'}
          </Button>
        </>
      )}

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}

function BrandImageControl({
  label,
  purpose,
  value,
  busy,
  onUpload,
  onRemove,
}: {
  label: string;
  purpose: string;
  value: string;
  busy: boolean;
  onUpload: (file: File | null) => void;
  onRemove: () => void;
}) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
      <Box
        sx={{
          width: 112,
          height: 112,
          flexShrink: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >
        {value ? (
          <Box component="img" src={value} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <ImageIcon color="disabled" sx={{ fontSize: 42 }} />
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{purpose}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button component="label" variant="outlined" disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <ImageIcon />}>
            {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                onUpload(event.target.files?.[0] || null);
                event.target.value = '';
              }}
            />
          </Button>
          {value && (
            <Button color="error" variant="text" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={onRemove}>
              Remove
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
